import type { Narrative } from '../hooks/useCompanyNarratives';
import type { CarouselRef } from '@/components/Carousel';
import Carousel from '@/components/Carousel';
import { StyledEmbla } from '@/components/Carousel/styled';
import { useTracking } from 'react-tracking';
import { NarrativeInvalidationBoundary } from '../../NarrativeInvalidationBoundary';
import { NarrativeMiniValuator } from '../../NarrativeMiniValuator';
import { useCallback, useRef, useState } from 'react';
import useUser from '@/hooks/useUser';
import { NarrativeCardIsomorphic } from '@/components/NarrativeCard';
import { useMe } from '@/components/User/components/Profile/components/CommunityProfile/hooks/useMe';

type Props = {
  canonicalUrl: string;
  narratives: Narrative[];
};

export const CompanyNarrativesList = ({ canonicalUrl, narratives }: Props) => {
  const { trackEvent } = useTracking();
  const { data: user } = useUser();
  const { data: me } = useMe({}, { enabled: user?.loggedIn });
  const [isDraggingDisabled, setIsDraggingDisabled] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const emblaRef = useRef<CarouselRef | null>(null);
  const narrativeCount = narratives.length;
  const hasMoreThanOneNarrative = narrativeCount > 1;
  const hasUserNarrative =
    user.loggedIn &&
    narratives.some(
      (narrative) =>
        narrative?.owner?.publicId === me?.me?.communityProfile?.publicId
    );
  const slideCount = narrativeCount + (hasUserNarrative ? 0 : 1);

  const handleOnClick = useCallback(
    (narrative: (typeof narratives)[number]) => {
      const { id, company } = narrative;
      return trackEvent({
        action: 'click',
        modifier: 'view',
        narrativeId: id,
        companyId: company?.id,
        uniqueSymbol: company?.tickerSymbol,
      });
    },
    [trackEvent]
  );

  const handleSliderInteraction = useCallback(
    (isInteracting: boolean) => {
      setIsDraggingDisabled(isInteracting);
      const currentIndex = emblaRef.current?.selectedScrollSnap();

      if (isInteracting || typeof currentIndex !== 'number')
        return setSelectedIndex(narrativeCount);

      setSelectedIndex(currentIndex);
    },
    [narrativeCount]
  );

  return (
    <NarrativeInvalidationBoundary
      onSetPrimary={({ invalidatePriority }) =>
        invalidatePriority(['narrative-company-report-list'])
      }
    >
      <StyledEmbla className="@container/carousel max-[375px]:-mx-x2 max-[375px]:[&>*>div::before]:opacity-60">
        <Carousel
          align="center"
          containScroll="trimSnaps"
          showArrows={hasMoreThanOneNarrative}
          showGradient={hasMoreThanOneNarrative}
          totalSlides={slideCount}
          draggable={!isDraggingDisabled}
          startIndex={selectedIndex}
          ref={emblaRef}
        >
          <div
            role="list"
            className="grid w-full gap-x1"
            style={{
              gridTemplateColumns: `repeat(${slideCount},max-content)`,
            }}
          >
            {narratives.map((narrative) => (
              <div
                role="listitem"
                className="w-[317px] @[375px]/carousel:w-[350px]"
                key={narrative.id}
              >
                <NarrativeCardIsomorphic
                  showCompanyIcon
                  narrative={narrative}
                  onClick={() => handleOnClick(narrative)}
                />
              </div>
            ))}
            {!hasUserNarrative && (
              <div
                role="listitem"
                className="w-[317px] @[375px]/carousel:w-[350px]"
              >
                <NarrativeMiniValuator
                  companyCanonicalUrl={canonicalUrl}
                  className="rounded-x1_5 bg-surface-2"
                  hideCompanySelect
                  onSliderInteraction={handleSliderInteraction}
                  titleAs="h2"
                />
              </div>
            )}
          </div>
        </Carousel>
      </StyledEmbla>
    </NarrativeInvalidationBoundary>
  );
};
